import { IAgentRuntime, Memory, Provider, ProviderResult, State, elizaLogger } from '@elizaos/core';
import { ZapperConfig } from '../types';
import { TransactionData, TransactionPerspective } from '../types/transaction';

export const transactionProvider: Provider = {
  name: 'transactionProvider',
  description:
    'Provides contextual transaction data and recent transaction history for wallet addresses',
  dynamic: true,
  get: async (runtime: IAgentRuntime, message: Memory, state: State): Promise<ProviderResult> => {
    elizaLogger.debug('transactionProvider::get');

    const zapperConfig: ZapperConfig = {
      apiKey: runtime.getSetting('ZAPPER_API_KEY'),
      apiUrl: runtime.getSetting('ZAPPER_API_URL') || 'https://public.zapper.xyz/graphql',
    };

    if (!zapperConfig.apiKey) {
      elizaLogger.error('ZAPPER_API_KEY not configured');
      return {
        text: 'Transaction data unavailable - API key not configured',
        data: { error: 'API key required' },
      };
    }

    // Extract wallet address from message or state
    const walletAddress = extractWalletAddress(message.content.text);

    if (!walletAddress) {
      elizaLogger.debug(
        'transactionProvider: No wallet address found in message, returning neutral context'
      );
      return {
        text: 'Transaction history context available when wallet address is provided',
        data: { provider: 'transactionProvider', status: 'neutral' },
      };
    }

    try {
      // Fetch recent transactions for context
      const recentTransactions = await fetchRecentTransactions(zapperConfig, walletAddress);

      if (!recentTransactions || recentTransactions.length === 0) {
        return {
          text: `No recent transactions found for ${walletAddress}`,
          data: {
            provider: 'transactionProvider',
            status: 'no-data',
            address: walletAddress,
            transactions: [],
          },
        };
      }

      // Analyze transaction patterns
      const transactionAnalysis = analyzeTransactionPatterns(recentTransactions);

      const summary = `Recent activity for ${walletAddress}:
📊 ${recentTransactions.length} transactions in recent history
💰 Total value: $${transactionAnalysis.totalValue.toFixed(2)}
🔄 Most active network: ${transactionAnalysis.mostActiveNetwork}
📈 Activity: ${transactionAnalysis.activityLevel}`;

      return {
        text: summary,
        data: {
          address: walletAddress,
          transactions: recentTransactions,
          analysis: transactionAnalysis,
        },
        values: {
          transactionCount: recentTransactions.length,
          totalValue: transactionAnalysis.totalValue,
          mostActiveNetwork: transactionAnalysis.mostActiveNetwork,
          activityLevel: transactionAnalysis.activityLevel,
        },
      };
    } catch (error) {
      elizaLogger.error('transactionProvider: Error fetching transaction data:', {
        error: error instanceof Error ? error.message : String(error),
        address: walletAddress,
        apiUrl: zapperConfig.apiUrl,
        hasApiKey: !!zapperConfig.apiKey,
      });
      return {
        text: 'Transaction data temporarily unavailable',
        data: {
          provider: 'transactionProvider',
          status: 'error',
          error: error instanceof Error ? error.message : String(error),
          address: walletAddress,
        },
      };
    }
  },
};

function extractWalletAddress(text: string | undefined): string | null {
  // Simple regex for Ethereum addresses
  if (!text) return null;
  const ethAddressRegex = /0x[a-fA-F0-9]{40}/;
  const match = text.match(ethAddressRegex);
  return match ? match[0] : null;
}

async function fetchRecentTransactions(
  config: ZapperConfig,
  address: string,
  limit: number = 10
): Promise<TransactionData[]> {
  const query = `
    query GetRecentTransactions(
      $subjects: [Address!]!
      $perspective: TransactionHistoryV2Perspective!
      $first: Int!
    ) {
      transactionHistoryV2(
        subjects: $subjects
        perspective: $perspective
        first: $first
      ) {
        edges {
          node {
            ... on TimelineEventV2 {
              id
              hash
              timestamp
              network
              fromAddress {
                address
              }
              toAddress {
                address
              }
              methodSighash
              value
              gas
              interpretation {
                processedDescription
                description
              }
              app {
                slug
                displayName
                url
              }
            }
            ... on ActivityTimelineEventDelta {
              id
              transactionHash
              network
              subject
              transactionBlockTimestamp
              nftCount
              fungibleCount
              from {
                address
              }
              to {
                address
              }
              fungibleDeltas {
                token {
                  id
                  address
                  symbol
                  name
                  decimals
                }
                amount
              }
              nftDeltas {
                collectionAddress
                tokenId
                amount
              }
            }
          }
        }
      }
    }
  `;

  const variables = {
    subjects: [address],
    perspective: 'All' as TransactionPerspective,
    first: limit,
  };

  const response = await fetch(config.apiUrl, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      'x-zapper-api-key': config.apiKey,
    },
    body: JSON.stringify({
      query,
      variables,
    }),
  });

  if (!response.ok) {
    const errorText = await response.text();
    elizaLogger.error('transactionProvider: HTTP error:', {
      status: response.status,
      statusText: response.statusText,
      error: errorText,
      url: config.apiUrl,
      address: address,
    });
    throw new Error(`HTTP error! status: ${response.status} - ${errorText}`);
  }

  const result: any = await response.json();

  if (result.errors) {
    elizaLogger.error('transactionProvider: GraphQL errors:', {
      errors: result.errors,
      address: address,
      url: config.apiUrl,
    });
    throw new Error(`GraphQL errors: ${JSON.stringify(result.errors)}`);
  }

  if (!result.data?.transactionHistoryV2?.edges) {
    return [];
  }

  return result.data.transactionHistoryV2.edges
    .map((edge: any) => {
      const node = edge.node;
      
      // Handle TimelineEventV2 nodes
      if (node.hash) {
        return {
          hash: node.hash,
          timestamp: node.timestamp,
          network: node.network || 'unknown',
          fromAddress: node.fromAddress.address,
          toAddress: node.toAddress?.address,
          methodSighash: node.methodSighash,
          value: node.value,
          gas: node.gas,
          interpretation: node.interpretation,
          app: node.app ? {
            name: node.app.displayName,
            logo: node.app.imgUrl,
            website: node.app.url,
          } : null,
        };
      }
      
      // Handle ActivityTimelineEventDelta nodes
      if (node.transactionHash) {
        return {
          hash: node.transactionHash,
          timestamp: node.transactionBlockTimestamp,
          network: node.network || 'unknown',
          fromAddress: node.from.address,
          toAddress: node.to?.address,
          methodSighash: null,
          value: node.fungibleDeltas?.length > 0 ? node.fungibleDeltas[0].amount.toString() : '0',
          gas: null,
          interpretation: {
            processedDescription: `${node.fungibleCount} fungible tokens, ${node.nftCount} NFTs`,
            description: `Activity with ${node.fungibleCount} tokens and ${node.nftCount} NFTs`
          },
          app: null,
          // Additional fields for ActivityTimelineEventDelta
          fungibleDeltas: node.fungibleDeltas,
          nftDeltas: node.nftDeltas,
          nftCount: node.nftCount,
          fungibleCount: node.fungibleCount,
        };
      }
      
      return null;
    })
    .filter(Boolean);
}

interface TransactionAnalysis {
  totalValue: number;
  mostActiveNetwork: string;
  activityLevel: 'Low' | 'Medium' | 'High';
  networkDistribution: Record<string, number>;
  avgTransactionValue: number;
  timeRange: {
    oldest: string;
    newest: string;
  };
}

function analyzeTransactionPatterns(transactions: TransactionData[]): TransactionAnalysis {
  if (transactions.length === 0) {
    return {
      totalValue: 0,
      mostActiveNetwork: 'unknown',
      activityLevel: 'Low',
      networkDistribution: {},
      avgTransactionValue: 0,
      timeRange: { oldest: '', newest: '' },
    };
  }

  // Calculate total value
  const totalValue = transactions.reduce((sum, tx) => {
    const value = Number.parseFloat(tx.value) || 0;
    return sum + value;
  }, 0);

  // Network distribution
  const networkDistribution: Record<string, number> = {};
  transactions.forEach((tx) => {
    networkDistribution[tx.network] = (networkDistribution[tx.network] || 0) + 1;
  });

  // Find most active network
  const mostActiveNetwork =
    Object.entries(networkDistribution).sort(([, a], [, b]) => b - a)[0]?.[0] || 'unknown';

  // Activity level based on transaction count
  const activityLevel: 'Low' | 'Medium' | 'High' =
    transactions.length >= 8 ? 'High' : transactions.length >= 4 ? 'Medium' : 'Low';

  // Average transaction value
  const avgTransactionValue = totalValue / transactions.length;

  // Time range
  const timestamps = transactions.map((tx) => tx.timestamp).sort();
  const timeRange = {
    oldest: timestamps[0] || '',
    newest: timestamps[timestamps.length - 1] || '',
  };

  return {
    totalValue,
    mostActiveNetwork,
    activityLevel,
    networkDistribution,
    avgTransactionValue,
    timeRange,
  };
}

// Helper function to format transaction context for other providers or actions
export function formatTransactionContext(transactions: TransactionData[]): string {
  if (transactions.length === 0) {
    return 'No recent transaction history available.';
  }

  const recent = transactions.slice(0, 3);
  const contextLines = recent.map((tx, index) => {
    const timeAgo = getTimeAgo(new Date(tx.timestamp));
    const description =
      tx.interpretation?.description ||
      tx.interpretation?.processedDescription ||
      'Transaction';
    return `${index + 1}. ${timeAgo}: ${description}`;
  });

  return `Recent transactions:\n${contextLines.join('\n')}`;
}

function getTimeAgo(date: Date): string {
  const now = new Date();
  const diffInSeconds = Math.floor((now.getTime() - date.getTime()) / 1000);

  if (diffInSeconds < 60) {
    return `${diffInSeconds}s ago`;
  }
  if (diffInSeconds < 3600) {
    return `${Math.floor(diffInSeconds / 60)}m ago`;
  }
  if (diffInSeconds < 86400) {
    return `${Math.floor(diffInSeconds / 3600)}h ago`;
  }
  return `${Math.floor(diffInSeconds / 86400)}d ago`;
}
